import { variableTranslations } from '../../constants/variableTranslations';
import { buildInstrumentResponsePresentation } from '../../utils/instrumentResponsePresentation';

const translatedUnitLabel = (unit, t) =>
  unit.kind === 'customNumeric'
    ? unit.label
    : t(variableTranslations[unit.key] || unit.label, { defaultValue: unit.label });

const translatedSelection = (selection, unit, t) =>
  selection === 'other'
    ? unit.otherLabel
    : t(variableTranslations[selection] || selection, { defaultValue: selection });

const inconsistencyTaskLabel = (taskKey, t) => {
  const responseKey = {
    vowels: 'vowelsInconsistency',
    sentences: 'sentencesInconsistency',
    extemporaneousSpeech: 'extemporaneousSpeechInconsistency',
  }[taskKey];
  return t(variableTranslations[responseKey] || taskKey, { defaultValue: taskKey });
};

const categoryLabel = (unit, t) => {
  const categoryKey = {
    pitch: { normal: 'normal_pitch', low: 'too_low_pitch', high: 'too_high_pitch' },
    loudness: { normal: 'normal_loudness', quiet: 'too_quiet_loudness', loud: 'too_loud_loudness' },
    resonance: { normal: 'normal_resonance', front: 'front_resonance', back: 'back_resonance' },
    nasality: { normal: 'normal_nasality', hyponasal: 'hyponasality', hypernasal: 'hypernasality' },
  }[unit.key]?.[unit.category];
  return t(categoryKey || unit.category, { defaultValue: unit.category });
};

export function buildCapeVrNativeRatingsExportRows(assessment, t) {
  const presentation = buildInstrumentResponsePresentation(assessment);
  if (presentation.kind !== 'normalized' || presentation.protocolKey !== 'cape-vr') return null;

  return presentation.units.flatMap(unit => {
    if (
      ['numeric', 'customNumeric'].includes(unit.kind) &&
      unit.responseState === 'answered' &&
      Number.isFinite(unit.value)
    ) {
      return [{
        attribute: translatedUnitLabel(unit, t),
        rating: `${unit.value}/100`,
        comment: unit.comment || '-',
      }];
    }

    if (unit.kind === 'category' && unit.responseState === 'answered' && unit.category) {
      return [{
        attribute: translatedUnitLabel(unit, t),
        rating: categoryLabel(unit, t),
        comment: unit.comment || '-',
      }];
    }

    if (unit.kind === 'inconsistency' && unit.responseState === 'none') {
      return [{
        attribute: translatedUnitLabel(unit, t),
        rating: t('none'),
        comment: '-',
      }];
    }

    if (unit.kind === 'inconsistency' && unit.responseState === 'present') {
      return (unit.affectedTasks || []).map(task => ({
        attribute: inconsistencyTaskLabel(task.taskKey, t),
        rating: t('present'),
        comment: task.description || '-',
      }));
    }

    if (unit.kind === 'reviewedList' && unit.responseState === 'answered') {
      return (unit.selected || []).flatMap(selection => {
        const attribute = translatedSelection(selection, unit, t);
        return attribute ? [{ attribute, rating: t('yes'), comment: '-' }] : [];
      });
    }

    if (unit.kind === 'text' && unit.responseState === 'answered' && unit.comment) {
      return [{
        attribute: translatedUnitLabel(unit, t),
        rating: '-',
        comment: unit.comment,
      }];
    }

    return [];
  });
}
